import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, CheckCircle, AlertTriangle, XCircle, Image, ChevronDown, Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useMyWhatsAppInstance } from '@/hooks/useMyWhatsAppInstance';

interface ReprocessingResult {
  processed: number;
  recovered: number;
  failed: number;
  notAvailable: number;
  errors: Array<{ messageId: string; error: string }>;
}

interface DryRunResult {
  messagesFound: number;
  messages: Array<{ id: string; body: string; from_number: string }>;
}

type Status = 'idle' | 'checking' | 'processing' | 'completed' | 'error';

export function MediaReprocessingCard() {
  const { instance } = useMyWhatsAppInstance();
  const [sessionName, setSessionName] = useState('');
  const [limit, setLimit] = useState(20);
  const [status, setStatus] = useState<Status>('idle');
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);
  const [results, setResults] = useState<ReprocessingResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  // Pre-fill session name from user's instance
  useState(() => {
    if (instance?.evolution_instance_name) {
      setSessionName(instance.evolution_instance_name);
    }
  });

  const handleCheck = async () => {
    if (!sessionName.trim()) {
      setErrorMessage('Nome da sessão é obrigatório');
      return;
    }

    setStatus('checking');
    setErrorMessage(null);
    setDryRunResult(null);
    setResults(null);

    try {
      const { data, error } = await supabase.functions.invoke('reprocess-wppconnect-media', {
        body: {
          sessionName: sessionName.trim(),
          limit,
          dryRun: true
        }
      });

      if (error) throw error;

      setDryRunResult({
        messagesFound: data.messagesFound || 0,
        messages: data.messages || []
      });
      setStatus('idle');
    } catch (err: any) {
      setErrorMessage(err.message || 'Erro ao verificar mensagens');
      setStatus('error');
    }
  };

  const handleReprocess = async () => {
    if (!sessionName.trim()) {
      setErrorMessage('Nome da sessão é obrigatório');
      return;
    }

    setStatus('processing');
    setErrorMessage(null);
    setResults(null);

    try {
      const { data, error } = await supabase.functions.invoke('reprocess-wppconnect-media', {
        body: {
          sessionName: sessionName.trim(),
          limit,
          dryRun: false
        }
      });

      if (error) throw error;

      setResults({
        processed: data.processed || 0,
        recovered: data.recovered || 0,
        failed: data.failed || 0,
        notAvailable: data.notAvailable || 0,
        errors: data.errors || []
      });
      setStatus('completed');
    } catch (err: any) {
      setErrorMessage(err.message || 'Erro ao reprocessar mídias');
      setStatus('error');
    }
  };

  const isLoading = status === 'checking' || status === 'processing';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Image className="h-5 w-5" />
          Reprocessar Mídias Antigas
        </CardTitle>
        <CardDescription>
          Recupere mídias de mensagens antigas que não foram baixadas corretamente
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription>
            <strong>Filtro automático ativo:</strong> O sistema busca apenas mensagens dos últimos 30 dias, 
            período em que o WhatsApp ainda mantém as mídias disponíveis. Mensagens mais antigas são ignoradas automaticamente.
          </AlertDescription>
        </Alert>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="sessionName">Nome da Sessão WPPConnect</Label>
            <Input
              id="sessionName"
              placeholder="Ex: minha_sessao"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="limit">Limite de Mensagens</Label>
            <Input
              id="limit"
              type="number"
              min={1}
              max={100}
              value={limit}
              onChange={(e) => setLimit(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))}
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">Máximo: 100 mensagens por vez</p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleCheck}
            disabled={isLoading || !sessionName.trim()}
          >
            {status === 'checking' ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Verificar Mensagens
          </Button>
          <Button
            onClick={handleReprocess}
            disabled={isLoading || !sessionName.trim()}
          >
            {status === 'processing' ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Iniciar Reprocessamento
          </Button>
        </div>

        {isLoading && (
          <div className="space-y-2">
            <Progress value={undefined} className="w-full" />
            <p className="text-sm text-muted-foreground text-center">
              {status === 'checking' ? 'Verificando mensagens...' : 'Reprocessando mídias...'}
            </p>
          </div>
        )}

        {errorMessage && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        {dryRunResult && (
          <Card className="bg-muted/50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Search className="h-5 w-5 text-primary" />
                <span className="font-medium">
                  {dryRunResult.messagesFound} mensagens encontradas
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {dryRunResult.messagesFound > 0 
                  ? 'Clique em "Iniciar Reprocessamento" para tentar recuperar as mídias.'
                  : 'Nenhuma mensagem com mídia pendente encontrada.'}
              </p>
            </CardContent>
          </Card>
        )}

        {results && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="bg-muted/50">
                <CardContent className="pt-4 text-center">
                  <div className="text-2xl font-bold">{results.processed}</div>
                  <div className="text-xs text-muted-foreground">Processadas</div>
                </CardContent>
              </Card>
              <Card className="bg-green-500/10 border-green-500/20">
                <CardContent className="pt-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-2xl font-bold text-green-600">{results.recovered}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">Recuperadas</div>
                </CardContent>
              </Card>
              <Card className="bg-yellow-500/10 border-yellow-500/20">
                <CardContent className="pt-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <AlertTriangle className="h-5 w-5 text-yellow-500" />
                    <span className="text-2xl font-bold text-yellow-600">{results.notAvailable}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">Indisponíveis</div>
                </CardContent>
              </Card>
              <Card className="bg-red-500/10 border-red-500/20">
                <CardContent className="pt-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <XCircle className="h-5 w-5 text-red-500" />
                    <span className="text-2xl font-bold text-red-600">{results.failed}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">Erros</div>
                </CardContent>
              </Card>
            </div>

            {results.errors.length > 0 && (
              <Collapsible open={showErrors} onOpenChange={setShowErrors}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-between">
                    <span>Ver detalhes dos erros ({results.errors.length})</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showErrors ? 'rotate-180' : ''}`} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                    {results.errors.map((err, idx) => (
                      <div key={idx} className="flex items-start gap-2 p-2 bg-red-500/5 rounded text-sm">
                        <Badge variant="outline" className="shrink-0 text-xs">
                          {err.messageId.slice(0, 8)}...
                        </Badge>
                        <span className="text-muted-foreground">{err.error}</span>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
